import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, Tabs, Tab, Menu, MenuItem, Typography, ListItemIcon } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useMenuItems, MenuLinkItem } from '../../../hooks/useMenuItems';
import { menuAppBarStyles as styles } from './MenuAppBarStyles';
import _ from 'lodash';

const MenuLinks: React.FC = () => {
  const { menuLinkItems } = useMenuItems();
  const location = useLocation();
  const [openAnchors, setOpenAnchors] = useState<Record<string, HTMLElement | null>>({});

  // Derive a normalized pathname without trailing slash for consistent comparisons
  const pathname = useMemo(() => {
    const p = location.pathname;
    return p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p;
  }, [location.pathname]);

  /**
   * Convert a link string (e.g. 'events') to a valid path ('/events').
   * Root ('/') is returned as-is.
   */
  const makePath = useCallback((link: string): string => {
    return link === '/' ? '/' : `/${link}`;
  }, []);

  /**
   * Derive the base path for a route (e.g. 'events/table' -> '/events')
   */
  const getBasePath = useCallback((link: string): string => {
    const parts = link.split('/');
    return `/${parts[0]}`;
  }, []);

  /**
   * Generate a stable key for each tab:
   * - For items with children, use kebab-cased label
   * - For items without children, use the link path directly
   */
  const getTabKey = useCallback((item: MenuLinkItem): string => {
    if (item.children && item.children.length) {
      return item.label.toLowerCase().replace(/\s+/g, '-');
    }
    return item.link!;
  }, []);

  /**
   * Determine which top-level tab is active.
   * 1. If a submenu path matches, activate its parent
   * 2. Else if a top-level link matches, activate that (using base path for consistency)
   * 3. Otherwise, no tab is active
   */
  const activeTabKey = useMemo<string | false>(() => {
    // 1) Match parent if any child route is active
    const parentMatch = _.find(menuLinkItems, (parent) =>
      Boolean(
        parent.children?.find((child) => {
          const base = getBasePath(child.link!);
          return pathname === base || pathname.startsWith(`${base}/`);
        })
      )
    );
    if (parentMatch) return getTabKey(parentMatch);

    // 2) Match top-level link (by base path)
    const directMatch = _.find(menuLinkItems, (item) => {
      if (!item.link) return false;
      const base = getBasePath(item.link);
      return pathname === base || pathname.startsWith(`${base}/`);
    });
    if (directMatch) return getTabKey(directMatch);

    return false;
  }, [menuLinkItems, pathname, getTabKey, getBasePath]);

  /**
   * Open dropdown for given parent label
   */
  const handleOpen = useCallback((label: string, e: React.MouseEvent<HTMLElement>) => {
    setOpenAnchors((prev) => ({ ...prev, [label]: e.currentTarget }));
  }, []);

  /**
   * Close dropdown for given parent label
   */
  const handleClose = useCallback((label: string) => {
    setOpenAnchors((prev) => ({ ...prev, [label]: null }));
  }, []);

  // Close all dropdowns on route change to avoid stale anchors
  useEffect(() => {
    setOpenAnchors({});
  }, [pathname]);

  return (
    <Box sx={styles.tabsContainer}>
      {/* Top-level tabs */}
      <Tabs value={activeTabKey} textColor="inherit" TabIndicatorProps={{ sx: styles.tabIndicator }} sx={styles.tabs}>
        {_.map(menuLinkItems, (item) => {
          const key = getTabKey(item);
          const isParent = Boolean(item.children?.length);
          const selected = key === activeTabKey;
          const tabSx = {
            ...styles.tab,
            ...(selected ? styles.tabSelected : styles.tabUnselected),
          };

          // Parent tabs render with a dropdown icon and click handler
          if (isParent) {
            const open = Boolean(openAnchors[item.label]);
            return (
              <Tab
                key={key}
                value={key}
                label={item.label}
                onClick={(e) => handleOpen(item.label, e)}
                icon={open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                iconPosition="end"
                sx={tabSx}
              />
            );
          }

          // Leaf tabs render as links
          return <Tab key={key} value={key} label={item.label} component={Link} to={makePath(item.link!)} sx={tabSx} />;
        })}
      </Tabs>

      {/* Dropdown menus for parent items */}
      {_.map(
        _.filter(menuLinkItems, (item) => Boolean(item.children?.length)),
        (parent) => {
          const key = getTabKey(parent);
          const anchorEl = openAnchors[parent.label];
          return (
            <Menu
              key={`menu-${key}`}
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => handleClose(parent.label)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: styles.menuPaper }}
            >
              {_.map(parent.children!, (sub) => {
                const base = getBasePath(sub.link!);
                const active = pathname === base || pathname.startsWith(`${base}/`);
                return (
                  <MenuItem
                    key={sub.label}
                    component={Link}
                    to={makePath(sub.link!)}
                    onClick={() => handleClose(parent.label)}
                    sx={{
                      ...styles.menuItem,
                      color: active ? '#1363DF' : 'inherit',
                      fontWeight: active ? 700 : 400,
                    }}
                  >
                    {sub.icon && <ListItemIcon sx={{ minWidth: 32, color: active ? '#1363DF' : 'inherit' }}>{sub.icon}</ListItemIcon>}
                    <Typography sx={{ flex: 1, color: active ? '#1363DF' : 'inherit', fontWeight: active ? 700 : 400 }}>{sub.label}</Typography>
                  </MenuItem>
                );
              })}
            </Menu>
          );
        }
      )}
    </Box>
  );
};

export default MenuLinks;
